import React from 'react'
import type {
  GetSecretIdAndBeenViewedByShortIdQuery,
  PublicVideoRequest,
} from 'apollo-gql'
import cn from 'classnames'
import { useTranslation } from 'lib/i18n'
import smoothscroll from 'smoothscroll-polyfill'

import Emoji, { EmojiType } from 'components/UI/Emoji/Emoji'
import Typography from 'components/UI/Typography/Typography'
import useEffectOnce from 'hooks/useEffectOnce'
import useResponsive from 'hooks/useResponsive'

import VideoActionButtons from './VideoActionButtons'
import VideoShare from './VideoShare'

import styles from './VideoRequestPage.module.css'

type VideoInformationBusinessInternalProps = {
  request: PublicVideoRequest
  showReviewButton: boolean
  contentRef: React.MutableRefObject<HTMLDivElement>
  review: GetSecretIdAndBeenViewedByShortIdQuery['market']['review']
  videoIsPlaying: boolean
}

export default function VideoInformationBusinessInternal({
  request,
  showReviewButton,
  contentRef,
  review,
  videoIsPlaying,
}: VideoInformationBusinessInternalProps): JSX.Element {
  const { t } = useTranslation()
  const { isMobile } = useResponsive()

  useEffectOnce(() => {
    smoothscroll.polyfill()
  })

  const { publicProfile: profile } = request

  const handleClick = () => {
    contentRef.current.scrollIntoView({ behavior: 'smooth' })
  }

  const emoji: EmojiType = 'movie-camera'

  const headerText = t(
    'VideoRequestPage.pageTitleForBusinessInternal',
    'Your Business Video from {{name}}!',
    {
      name: profile.name,
    },
  )

  return (
    <>
      {!isMobile && (
        <div className={styles.content}>
          <Emoji emoji={emoji} className={styles.headingIcon} />

          <Typography.H1 className={styles.contentHeading}>
            {headerText}
          </Typography.H1>

          <div className={styles.businessNameContainer}>
            <Typography.BodyBold>
              {t('VideoRequestPage.to', 'To:')}
            </Typography.BodyBold>{' '}
            <div className={styles.businessName}>{request?.toName}</div>
            {request?.fromName && (
              <>
                <div style={{ width: 56 }} />
                <Typography.BodyBold>
                  {t('VideoRequestPage.from', 'From:')}
                </Typography.BodyBold>{' '}
                <div className={styles.businessName}>{request.fromName}</div>
              </>
            )}
          </div>

          <VideoShare
            request={request}
            className={styles.videoInfoSharing}
            hideSocialMediaLinks
          />

          <div className={styles.businessExplainer}>
            {t(
              'VideoRequestPage.businessInternalExplainer',
              'Planning an event and need similar vidoes? Get in touch with our memmo specialists!',
            )}
          </div>

          <div className={styles.content}>
            <VideoActionButtons
              showReviewButton={showReviewButton}
              review={review}
              request={request}
            />
          </div>
        </div>
      )}
      {isMobile && (
        <div className={styles.orderInfoButtonWrapper}>
          <div
            className={cn(styles.content, {
              [styles.hidden]: videoIsPlaying,
            })}
          >
            <Emoji emoji={emoji} className={styles.emoji} />
            <Typography.H3 theme="white">{headerText}</Typography.H3>
            <div className={styles.arrowDown}>
              <button
                type="button"
                aria-label="scroll down"
                className={styles.arrowButton}
                onClick={handleClick}
              />
            </div>
          </div>
        </div>
      )}
    </>
  )
}
